import apiClient from './apiClient';
import { Logro, UsuarioLogro, NivelUsuario, PuntosOtorgadosResponse } from '../types/logro';

/**
 * Forma de `GET /gamificacion/estado`, segun el contrato del backend.
 *
 * Se declara aunque el endpoint hoy responda **501**: el tipo documenta lo que
 * tiene que devolver cuando se implemente, y por eso `JuegosPage` compila sin
 * inventarse un objeto. Nótese que NO se pone ningun valor por defecto aqui: un
 * `catch` que devuelve ceros es lo que produjo los numeros falsos de P8.
 */
export interface EstadoGamificacion {
  rachaActividad: number;
  minutosRestantesHoy: number;
  posicionRanking: number;
  misionesCompletadasHoy: number;
  misionesTotalesHoy: number;
}

export const gamificacionService = {
  async getLogros(): Promise<Logro[]> {
    const response = await apiClient.get<Logro[]>('/gamificacion/logros');
    return response.data;
  },

  async getUserLogros(): Promise<UsuarioLogro[]> {
    const response = await apiClient.get<UsuarioLogro[]>('/gamificacion/logros/usuario');
    return response.data;
  },

  async getNivel(): Promise<NivelUsuario> {
    const response = await apiClient.get<NivelUsuario>('/gamificacion/nivel');
    return response.data;
  },

  async otorgarPuntos(tipoActividad: string, cantidad: number): Promise<any> {
    const response = await apiClient.post('/gamificacion/puntos', { tipoActividad, cantidad });
    return response.data;
  },

  async verificarLogros(): Promise<any> {
    const response = await apiClient.post('/gamificacion/verificar-logros');
    return response.data;
  },

  async getEstadisticas(): Promise<any> {
    const response = await apiClient.get('/gamificacion/estadisticas');
    return response.data;
  },

  async getLeaderboard(): Promise<RespuestaLeaderboard> {
    const response = await apiClient.get('/gamificacion/leaderboard');
    return response.data;
  },

  async addPuntos(puntos: number, tipoJuego: string = 'JUEGO_CALMA_MATCH', combo: number = 0, duracion: number = 0): Promise<PuntosOtorgadosResponse> {
    const response = await apiClient.post<PuntosOtorgadosResponse>('/gamificacion/puntos', { tipoActividad: tipoJuego, cantidad: puntos, combo, duracion });
    return response.data;
  },

  /**
   * Misiones diarias. **Sin implementacion en el backend** (P8: responde 501).
   *
   * ANTES: el `catch` devolvia `[]`, y `JuegosPage` pintaba "No hay misiones
   * disponibles por ahora". Eso es una afirmacion sobre el mundo que el backend
   * nunca hizo: el endpoint no decia que no hay misiones, decia que no existe.
   *
   * Ahora el error se propaga. Es la unica forma de que el llamador distinga
   * "no hay misiones" de "no hay sistema de misiones".
   */
  async getMisionesDiarias(): Promise<Mision[]> {
    const response = await apiClient.get<Mision[]>('/gamificacion/misiones-diarias');
    return response.data;
  },

  /** Sin implementacion en el backend (501). Mismo motivo que el anterior. */
  async getRankingCalmaMatch(): Promise<EntradaRanking[]> {
    const response = await apiClient.get<EntradaRanking[]>('/gamificacion/ranking/calma-match');
    return response.data;
  },

  /**
   * Estado de gamificacion. **Sin implementacion en el backend** (501).
   *
   * ANTES: el `catch` inventaba `{ rachaActividad: 0, minutosRestantesHoy: 30,
   * posicionRanking: 0, misionesCompletadasHoy: 0, misionesTotalesHoy: 3 }`, y
   * `JuegosPage` lo mostraba. Cuatro numeros falsos en pantalla, uno de ellos
   * "30 min", que parece un limite real que el estudiante creeria que agotar.
   *
   * Se propaga el error. El tipo de retorno dice que la forma es la del backend,
   * no un contrato inventado aqui.
   */
  async getEstadoGamificacion(): Promise<EstadoGamificacion> {
    const response = await apiClient.get<EstadoGamificacion>('/gamificacion/estado');
    return response.data;
  },

  async registrarSesionJuego(tipoJuego: string, puntos: number, combo: number, duracion: number): Promise<any> {
    try {
      const response = await apiClient.post('/gamificacion/sesion-juego', { 
        tipoJuego, 
        puntos, 
        combo, 
        duracion 
      });
      return response.data;
    } catch (error) {
      console.log('Sesion juego endpoint not available, returning mock response');
      return {
        puntosGanados: Math.floor(puntos / 10),
        nivelSubido: false,
        logrosDesbloqueados: []
      };
    }
  },
};

/**
 * Forma de un item de `GET /gamificacion/misiones-diarias`, segun como la
 * consumia `JuegosPage` antes de P8 (leia `id`, `titulo`, `descripcion`,
 * `completada`, `puntos`, `progreso` y `objetivo` de un `any`).
 *
 * Se declara aunque el endpoint hoy responda **501**: documenta el contrato que
 * habra que cumplir cuando exista, y sustituye al `any` que hacia legitimo leer
 * campos que el backend nunca promised.
 */
export interface Mision {
  id?: number;
  titulo?: string;
  descripcion?: string;
  completada?: boolean;
  puntos?: number;
  progreso?: number;
  objetivo?: number;
}
/**
 * Fila de `GET /gamificacion/leaderboard`, segun como la leia `JuegosPage`
 * antes de P8: `id`, `nombre` y `puntos` de un `any`.
 *
 * El endpoint responde **501** hoy. El tipo existe para que, cuando se implemente,
 * el consumidor no tenga que inventarse la forma.
 */
export interface EntradaRanking {
  id: number;
  nombre: string;
  puntos: number;
  /** Marca la fila del propio estudiante, para poderresaltarla. */
  esUsuario?: boolean;
}

/** Forma de la respuesta de `leaderboard` cuando exista. */
export interface RespuestaLeaderboard {
  leaderboard: EntradaRanking[];
}
